'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { logger } from './logger';

export interface DisplayConfig {
  chartDecimalPlaces: number;
  tableDecimalPlaces: number;
}

export interface GlobalSettingsData {
  state: string;
  historicalYearStart: number;
  historicalYearEnd: number;
  firstYearProjections: number;
  projectionYears: number;
  mediumTermYear: number;
  debtTarget: number;
  shockInputs: {
    revenueShockType: 'Stdev' | 'Manual';
    revenueShockStdev: number;
    revenueShockManual: number;
    revenueShockDuration: number;
    intergovernmentalRevenueShockType: 'Stdev' | 'Manual';
    intergovernmentalRevenueShockStdev: number;
    intergovernmentalRevenueShockManual: number;
    intergovernmentalRevenueShockDuration: number;
    expenditureShockType: 'Stdev' | 'Manual';
    expenditureShockStdev: number;
    expenditureShockManual: number;
    expenditureShockDuration: number;
    interestRateShockBps: number;
  };
  borrowingInstruments: Array<{
    id: number;
    instrument: string;
    currency: string;
    interestRate: number;
    maturity: number;
    type: string;
    share: number;
  }>;
  stateMetadata: {
    stateName: string;
    debtRating: string;
    ratingAgency: string;
    lastUpdated: string;
    description: string;
  };
  displayConfig?: DisplayConfig;
  timePeriodConstraints?: {
    projectionYearsMin: number;
    projectionYearsMax: number;
    historicalBeginMin: number;
    historicalBeginMax: number;
    historicalEndMin: number;
    historicalEndMax: number;
  };
}

interface GlobalSettingsContextType {
  settings: GlobalSettingsData; // Settings are guaranteed to be loaded at app startup
  updateSettings: (newSettings: Partial<GlobalSettingsData>, currentModule?: string, skipRecalculation?: boolean) => Promise<void>;
  updateSetting: (field: string, value: string | number, currentModule?: string) => Promise<void>;
  resetSettings: (currentModule?: string) => Promise<void>;
  getTableYears: () => { actualYears: number[]; projectionYears: number[] };
  loading: boolean;
  error: string | null;
}

const GlobalSettingsContext = createContext<GlobalSettingsContextType | undefined>(undefined);

export function GlobalSettingsProvider({ children }: { children: ReactNode }) {
  // Settings are loaded from backend API - no hardcoded values
  const [settings, setSettings] = useState<GlobalSettingsData | null>(null);
  const [loading, setLoading] = useState(true); // Start with loading = true
  const [error, setError] = useState<string | null>(null);

  // Load settings from API on mount - this is the source of truth
  // Settings MUST be loaded before rendering children
  React.useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { apiService } = await import('./api-service');
      const data = await apiService.getGlobalSettings();
      
      if (data) {
        setSettings(data as GlobalSettingsData);
      } else {
        setError('Failed to load settings');
      }
    } catch (err) {
      logger.error('Error loading settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  // Don't render children until settings are loaded
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading application settings...</p>
        </div>
      </div>
    );
  }

  // Show error state if settings failed to load
  if (error || !settings) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <p className="text-destructive text-lg font-semibold mb-2">Failed to load settings</p>
          <p className="text-muted-foreground mb-4">{error || 'Settings not available'}</p>
          <button
            onClick={() => loadSettings()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const updateSettings = async (newSettings: Partial<GlobalSettingsData>, currentModule?: string, skipRecalculation?: boolean) => {
    try {
      setLoading(true);
      setError(null);
      
      const { apiService } = await import('./api-service');
      const data = await apiService.updateGlobalSettings(newSettings, currentModule);
      
      if (data) {
        setSettings(data as GlobalSettingsData);
        
        // Handle recalculation if needed (unless skipRecalculation is true)
        if (data.recalculationNeeded && !skipRecalculation) {
          // Dispatch events for all modules that need recalculation
          if (data.recalculationNeeded.scenarioAnalysis) {
            window.dispatchEvent(new CustomEvent('settings-updated', { 
              detail: { 
                module: 'scenario-analysis',
                recalculationNeeded: data.recalculationNeeded 
              } 
            }));
          }
          
          if (data.recalculationNeeded.stochasticProjection) {
            window.dispatchEvent(new CustomEvent('settings-updated', { 
              detail: { 
                module: 'stochastic-projection',
                recalculationNeeded: data.recalculationNeeded 
              } 
            }));
          }
          
          if (data.recalculationNeeded.comparisonCharts) {
            window.dispatchEvent(new CustomEvent('settings-updated', { 
              detail: { 
                module: 'comparison-charts',
                recalculationNeeded: data.recalculationNeeded 
              } 
            }));
          }
        }
      } else {
        setError('Failed to update settings');
      }
    } catch (err) {
      logger.error('Error updating settings:', err);
      setError('Failed to update settings');
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (field: string, value: string | number, currentModule?: string) => {
    // Settings are guaranteed to be loaded at this point
    
    let newSettings: Partial<GlobalSettingsData>;
    
    if (field.includes('.')) {
      const [section, subField] = field.split('.');
      newSettings = {
        [section]: {
          ...(settings[section as keyof GlobalSettingsData] as Record<string, unknown>),
          [subField]: value
        }
      };
    } else {
      newSettings = {
        [field]: value
      };
    }

    await updateSettings(newSettings, currentModule);
  };

  const resetSettings = async (currentModule?: string) => {
    try {
      setLoading(true);
      setError(null);
      
      const { apiService } = await import('./api-service');
      const data = await apiService.resetGlobalSettings(currentModule);
      
      if (data) {
        setSettings(data as GlobalSettingsData);
        
        // Handle recalculation if needed
        if (data.recalculationNeeded) {
          // Dispatch events for all modules that need recalculation
          if (data.recalculationNeeded.scenarioAnalysis) {
            window.dispatchEvent(new CustomEvent('settings-updated', { 
              detail: { 
                module: 'scenario-analysis',
                recalculationNeeded: data.recalculationNeeded 
              } 
            }));
          }
          
          if (data.recalculationNeeded.stochasticProjection) {
            window.dispatchEvent(new CustomEvent('settings-updated', { 
              detail: { 
                module: 'stochastic-projection',
                recalculationNeeded: data.recalculationNeeded 
              } 
            }));
          }
          
          if (data.recalculationNeeded.comparisonCharts) {
            window.dispatchEvent(new CustomEvent('settings-updated', { 
              detail: { 
                module: 'comparison-charts',
                recalculationNeeded: data.recalculationNeeded 
              } 
            }));
          }
        }
      } else {
        setError('Failed to reset settings');
      }
    } catch (error) {
      logger.error('Error resetting settings:', error);
      setError('Failed to reset settings');
    } finally {
      setLoading(false);
    }
  };

  const getTableYears = () => {
    // Settings are guaranteed to be loaded at app startup
    const actualYears: number[] = [];
    const projectionYears: number[] = [];
    
    // Generate actual years from historical range
    for (let year = settings.historicalYearStart; year <= settings.historicalYearEnd; year++) {
      actualYears.push(year);
    }
    
    // Generate projection years
    for (let year = settings.firstYearProjections; year < settings.firstYearProjections + settings.projectionYears; year++) {
      projectionYears.push(year);
    }
    
    return { actualYears, projectionYears };
  };

  const value: GlobalSettingsContextType = {
    settings,
    updateSettings,
    updateSetting,
    resetSettings,
    getTableYears,
    loading,
    error
  };

  return (
    <GlobalSettingsContext.Provider value={value}>
      {children}
    </GlobalSettingsContext.Provider>
  );
}

export function useGlobalSettings() {
  const context = useContext(GlobalSettingsContext);
  if (context === undefined) {
    throw new Error('useGlobalSettings must be used within a GlobalSettingsProvider');
  }
  return context;
}
